import React, { useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { subnetsAPI, ipAssignmentAPI, Subnet } from '../services/api';

interface SubnetWithStats extends Subnet {
  percentageUsed?: number;
}

const ITIPAddresses: React.FC = () => {
  const [subnets, setSubnets] = useState<SubnetWithStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [cidrFilter, setCidrFilter] = useState('');
  const [gatewayFilter, setGatewayFilter] = useState('');

  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedSubnet, setSelectedSubnet] = useState<SubnetWithStats | null>(null);
  const [availableIPs, setAvailableIPs] = useState<string[]>([]);
  const [ipsLoading, setIpsLoading] = useState(false);
  const [ipsError, setIpsError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await subnetsAPI.getAll();
      const withStats: SubnetWithStats[] = data.map((s) => ({
        ...s,
        percentageUsed: s.totalIPs > 0 ? Math.round((s.usedIPs / s.totalIPs) * 100) : 0
      }));
      setSubnets(withStats);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError(e?.response?.data?.error || 'Greška pri učitavanju subnetova');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openDetails = async (row: SubnetWithStats) => {
    setSelectedSubnet(row);
    setDetailsVisible(true);
    setIpsLoading(true);
    setIpsError('');
    try {
      const res = await ipAssignmentAPI.getAvailableIPs(row._id, 0);
      setAvailableIPs(res.availableIPs || []);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      setIpsError(e?.response?.data?.error || 'Greška pri učitavanju IP adresa');
      setAvailableIPs([]);
    } finally {
      setIpsLoading(false);
    }
  };

  const filteredSubnets = subnets.filter((s) => {
    const nameMatch = !nameFilter || s.name.toLowerCase().includes(nameFilter.toLowerCase());
    const cidrMatch = !cidrFilter || s.cidr.toLowerCase().includes(cidrFilter.toLowerCase());
    const gatewayMatch = !gatewayFilter || s.gateway.toLowerCase().includes(gatewayFilter.toLowerCase());
    return nameMatch && cidrMatch && gatewayMatch;
  });

  const usedBody = (row: SubnetWithStats) => {
    return (
      <span>
        {row.usedIPs} / {row.totalIPs}
      </span>
    );
  };

  const freeBody = (row: SubnetWithStats) => {
    return <span>{row.freeIPs}</span>;
  };

  const nameBody = (row: SubnetWithStats) => (
    <div className="flex align-items-center gap-2">
      <Button
        icon="pi pi-eye"
        rounded
        text
        aria-label="Detalji"
        onClick={() => openDetails(row)}
      />
      <span>{row.name}</span>
    </div>
  );

  const detailsFooter = (
    <div className="flex justify-content-end">
      <Button label="Zatvori" onClick={() => setDetailsVisible(false)} />
    </div>
  );

  return (
    <div className="p-4">
      <Card>
        <h2 className="mb-3">IP Adrese</h2>

        {error && <Message severity="error" text={error} className="mb-3" />}

        <div className="grid mb-3">
          <div className="col-12 md:col-4">
            <InputText
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="Pretraži po nazivu"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-4">
            <InputText
              value={cidrFilter}
              onChange={(e) => setCidrFilter(e.target.value)}
              placeholder="Pretraži po CIDR"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-4">
            <InputText
              value={gatewayFilter}
              onChange={(e) => setGatewayFilter(e.target.value)}
              placeholder="Pretraži po gateway"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <DataTable
          value={filteredSubnets}
          loading={loading}
          paginator
          rows={10}
          emptyMessage="Nema subnetova"
          className="p-datatable-sm"
          responsiveLayout="stack"
          breakpoint="768px"
        >
          <Column field="name" header="Naziv Subnet-a" body={nameBody} sortable />
          <Column field="cidr" header="CIDR" sortable />
          <Column field="gateway" header="Gateway" sortable />
          <Column field="totalIPs" header="Ukupno IP" sortable />
          <Column header="Korišćeno" body={usedBody} sortable />
          <Column header="Slobodno" body={freeBody} sortable />
        </DataTable>
      </Card>

      <Dialog
        header={
          selectedSubnet
            ? `IP Adrese - ${selectedSubnet.name}`
            : 'IP Adrese'
        }
        visible={detailsVisible}
        style={{ width: '800px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        onHide={() => setDetailsVisible(false)}
        footer={detailsFooter}
        modal
        draggable={false}
      >
        {selectedSubnet && (
          <>
            <p>
              <strong>Subnet:</strong> {selectedSubnet.name} ({selectedSubnet.cidr})
            </p>
            <p>
              <strong>Gateway:</strong> {selectedSubnet.gateway}
            </p>
            {selectedSubnet.dnsServers && selectedSubnet.dnsServers.length > 0 && (
              <p>
                <strong>DNS Serveri:</strong> {selectedSubnet.dnsServers.join(', ')}
              </p>
            )}

            <h4 className="mt-3 mb-2">IP Adrese</h4>
            {ipsError && <Message severity="error" text={ipsError} className="mb-2" />}
            {ipsLoading ? (
              <p>Učitavanje IP adresa...</p>
            ) : availableIPs.length === 0 ? (
              <p>No available options</p>
            ) : (
              <div className="grid">
                {availableIPs.map((ip) => (
                  <div key={ip} className="col-12 md:col-4 mb-2">
                    <Tag value={ip} />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Dialog>
    </div>
  );
};

export default ITIPAddresses;






